import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { format } from 'date-fns';
import './TournamentCard.css';

// Import the banner image
import bannerImage from '../../../assets/images/tournament-banner-image.png';
// Import the SFL logo
import sflLogo from '../../../assets/images/SFL Logo Blue.png';
import { getImageUrl } from '../../../utils/imageUrl';

// Helper function to get tournament thumbnail URL
const getTournamentThumbnailUrl = (tournament: Tournament) => {
  if (tournament.thumbnail && tournament.thumbnail !== 'thumbnail') {
    return getImageUrl(tournament.thumbnail);
  }
  return null;
};

interface Tournament {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  thumbnail?: string; // Add thumbnail field
  location: string;
  startDate: string;
  endDate: string;
  status: string;
  sports: string;
  venue?: string;
  coachManagerName?: string;
  ageCategories?: string[];
  isTeamRegistered?: boolean;
}

interface TournamentCardProps {
  tournament: Tournament;
  onViewDetails: () => void;
  onRegisterNow: () => void;
  showRegisterButton: boolean;
  activeTab?: string;
  index?: number;
}

const TournamentCard: React.FC<TournamentCardProps> = ({
  tournament,
  onViewDetails,
  onRegisterNow,
  showRegisterButton = false,
  activeTab,
  index
}) => {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM, yyyy');
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status: string) => {
    if (activeTab === 'past') {
      return <div className="status-badge past">PAST</div>;
    } else if (activeTab === 'registered') {
      return <div className="status-badge registered">REGISTERED</div>;
    } else {
      return <div className="status-badge upcoming">UPCOMING</div>;
    }
  };

  const getAgeCategoryDisplay = (categories?: string[]) => {
    if (!categories || categories.length === 0) return 'All Ages';
    return categories.join(', ');
  };

  const getPlayerIllustrationStyle = () => {
    if (index === 0) {
      return { background: 'linear-gradient(45deg, #FF6B35, #FF8C42)' }; // Red jersey
    } else if (index === 1) {
      return { background: 'linear-gradient(45deg, #FFD700, #FFA500)' }; // Yellow jersey
    } else {
      return { background: 'linear-gradient(45deg, #FF6B35, #FF8C42)' }; // Red jersey
    }
  };

  return (
    <Card 
      className="tournament-card"
      style={{
        backgroundColor: tournament.isTeamRegistered ? '#F0F0F0' : undefined
      }}
    >
      {/* Tournament Banner */}
      <div className="tournament-banner">
        <img 
          src={getTournamentThumbnailUrl(tournament) || bannerImage}
          alt={`${tournament.name} banner`}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = bannerImage;
          }}
        />
        
        {/* Tournament Logo - moved inside banner */}
        <div className="tournament-logo">
          <img 
            src={tournament.logo || sflLogo} 
            alt={tournament.name}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = sflLogo;
            }}
          />
        </div>
      </div>

      {/* Tournament Details */}
      <Card.Body className="tournament-details">
        {/* Status Tag */}
        <div className="upcoming-tag">
          <span>{(tournament.status || activeTab || 'upcoming').toString().toUpperCase()}</span>
        </div>

        <h5 className="tournament-title">{tournament.name}</h5>
        
        <div className="info-grid">
          <div className="info-item">
            <div className="info-icon">
              <i className="ri-team-line"></i>
            </div>
            <div className="info-content">
              <span className="info-label">Sports</span>
              <span className="info-value">{tournament.sports || 'Football'}</span>
            </div>
          </div>

          <div className="info-item">
            <div className="info-icon">
              <i className="ri-user-line"></i>
            </div>
            <div className="info-content">
              <span className="info-label">Age Category</span>
              <span className="info-value">{getAgeCategoryDisplay(tournament.ageCategories)}</span>
            </div>
          </div>

          <div className="info-item">
            <div className="info-icon">
              <i className="ri-calendar-line"></i>
            </div>
            <div className="info-content">
              <span className="info-label">Date</span>
              <span className="info-value">{formatDate(tournament.startDate)}</span>
            </div>
          </div>

          <div className="info-item">
            <div className="info-icon">
              <i className="ri-map-pin-line"></i>
            </div>
            <div className="info-content">
              <span className="info-label">Location</span>
              <span className="info-value">{tournament.location}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="tournament-actions">
          <button 
            className="view-details-btn"
            onClick={onViewDetails}
            style={{
              color: tournament.isTeamRegistered ? '#1A1A1A' : undefined
            }}
          >
            View Details
          </button>
          {showRegisterButton && (
            <button 
              className="register-btn"
              onClick={onRegisterNow}
              style={{
                backgroundColor: tournament.isTeamRegistered ? '#1A1A1A' : undefined,
                color: tournament.isTeamRegistered ? '#FFFFFF' : undefined,
                borderColor: tournament.isTeamRegistered ? '#1A1A1A' : undefined
              }}
              onMouseEnter={(e) => {
                if (tournament.isTeamRegistered) {
                  e.currentTarget.style.backgroundColor = '#1A1A1A';
                  e.currentTarget.style.color = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#1A1A1A';
                }
              }}
              onMouseLeave={(e) => {
                if (tournament.isTeamRegistered) {
                  e.currentTarget.style.backgroundColor = '#1A1A1A';
                  e.currentTarget.style.color = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#1A1A1A';
                }
              }}
            >
              {tournament.isTeamRegistered ? 'Manage' : 'Register Now'}
            </button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default TournamentCard; 